import React from 'react';
import { styled } from '@mui/material/styles';
import {
	Avatar,
	Box,
	Button,
	Card,
	CardContent,
	Typography,
} from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import Divider from '@mui/material/Divider';

const DashboardContainer = styled(Box)(({ theme }) => ({
	minHeight: '100vh',
	backgroundColor: '#fff',
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'space-between',
	flexDirection: 'column',
	//padding: theme.spacing(2),
}));

const HeaderCard = styled(Card)(({ theme }) => ({
	width: '100%',
	maxWidth: 500,
	marginBottom: 0,
	boxShadow: 'none',
	borderRadius: theme.shape.borderRadius * 2,
	padding: theme.spacing(0, 2),
}));

const HeaderContent = styled(CardContent)(() => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	padding: 0,
	borderWidth: 0,
}));

const HeaderAvatar = styled(Avatar)(() => ({
	backgroundColor: '#f5f5f5',
	width: 40,
	height: 40,
}));

const HeaderIcon = styled(PersonOutlinedIcon)(() => ({
	color: '#757575',
	fontSize: 25,
}));

const HeaderTitle = styled(Typography)(() => ({
	fontWeight: 600,
	fontSize: 14,
}));

const HeaderSubtitle = styled(Typography)(() => ({
	fontSize: 10,
}));

const WalletSection = styled(Box)(({ theme }) => ({
	marginTop: theme.spacing(4),
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	textAlign: 'center',
	padding: theme.spacing(2),
}));

const WalletAvatar = styled(Avatar)(() => ({
	backgroundColor: '#f5f5f5',
	width: 64,
	height: 64,
}));

const WalletIcon = styled(AccountBalanceWalletOutlinedIcon)(() => ({
	color: '#757575',
	fontSize: 36,
}));

const WalletTitle = styled(Typography)(() => ({
	fontWeight: 600,
	marginTop: 16,
}));

const WalletDescription = styled(Typography)(() => ({
	maxWidth: 300,
	marginTop: 8,
	fontSize: 10,
}));

const ConnectButton = styled(Button)(({ theme }) => ({
	marginTop: theme.spacing(2),
	backgroundColor: '#1A73E8',
	color: '#fff',
	borderRadius: theme.shape.borderRadius * 2,
	textTransform: 'none',
	fontWeight: 600,
	fontSize: 14,
	padding: '14px 20px',
	'&:hover': {
		backgroundColor: '#1558b0',
	},
}));

const StyledConnectButton = styled(ConnectButton)(() => ({
	fontSize: 8,
	marginTop: 0,
	padding: '7px 10px',
}));

const WalletSupported = styled(Typography)(() => ({
	marginTop: 4,
	fontSize: 8,
}));

const TopBar = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	margin: theme.spacing(0, 0, 2),
	padding: theme.spacing(2),
}));

const Title = styled(Typography)(({ theme }) => ({
	fontSize: theme.typography.pxToRem(18),
	fontWeight: 600,
	letterSpacing: '-0.01em',
}));

export const ConnectPanel: React.ComponentType<{
	onOpenWallet: () => void;
}> = ({ onOpenWallet }) => {
	const { wallets } = useSelector((state: RootState) => state.vault);

	return (
		<DashboardContainer>
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					padding: 0,
					width: '100%',
					border: '0px solid #000',
				}}
			>
				<TopBar>
					<Title>Trading Dashboard</Title>
					<ChevronRightRounded fontSize="small" color="action" />
				</TopBar>

				<HeaderCard>
					<HeaderContent>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 2,
							}}
						>
							<HeaderAvatar>
								<HeaderIcon />
							</HeaderAvatar>
							<Box>
								<HeaderTitle variant="subtitle1">
									Not Connected
								</HeaderTitle>
								<HeaderSubtitle
									variant="body2"
									color="text.secondary"
								>
									Connect and start trading
								</HeaderSubtitle>
							</Box>
						</Box>
						<StyledConnectButton
							size="small"
							onClick={onOpenWallet}
						>
							Connect
						</StyledConnectButton>
					</HeaderContent>
				</HeaderCard>
				<Divider />
			</div>

			<WalletSection>
				<WalletAvatar>
					<WalletIcon />
				</WalletAvatar>
				<WalletTitle variant="subtitle1">
					Connect Your Wallet
				</WalletTitle>
				<WalletDescription variant="caption" color="text.secondary">
					Connect your wallet to access trading features, view your
					portfolio, and track your trading history
				</WalletDescription>
				<ConnectButton size="large" fullWidth onClick={onOpenWallet}>
					Connect Wallet
				</ConnectButton>
				<WalletSupported variant="caption" color="text.secondary">
					Supported:{' '}
					{wallets
						.filter((d) => !d.match('Mobile'))
						.toLocaleString()
						.toLocaleUpperCase()}
				</WalletSupported>
			</WalletSection>
			<div />
			<div />
		</DashboardContainer>
	);
};
